import React, {
	useContext,
	useMemo,
	useCallback,
	useState,
	useEffect,
} from "react";
import { toast } from "react-toastify";
import { UserContext } from "../../App";
import {
	reactAnnouncement,
	reactLevelComment,
	reactProfileComment,
} from "../../api";

// constants
import { UserConstants } from "../../constants";

const CommentReaction = ({ document, setDocuments, type }) => {
	const { currentUserData } = useContext<any>(UserContext);
	const currentUserId = currentUserData?._id;

	const userRoles = useMemo(() => currentUserData?.mainRoles || [], [currentUserData]);
	const userIsMuted = useMemo(
		() => userRoles.includes(UserConstants.Punishments.MUTED),
		[userRoles],
	);
	const [isLiked, setIsLiked] = useState(
		document?.user_likes?.includes(currentUserId) || false,
	);
	const [isDisliked, setIsDisliked] = useState(
		document?.user_dislikes?.includes(currentUserId) || false,
	);
    const [likeCount, setLikeCount] = useState(document?.user_likes?.length || 0);
    const [dislikeCount, setDislikeCount] = useState(document?.user_dislikes?.length || 0);

	useEffect(() => {
		// listen for changes and update states accordingly
		setIsLiked(document?.user_likes?.includes(currentUserId) || false);
		setIsDisliked(
			document?.user_dislikes?.includes(currentUserId) || false,
		);
		setLikeCount(document?.user_likes?.length || 0);
		setDislikeCount(document?.user_dislikes?.length || 0);
	}, [document?.user_likes, document?.user_dislikes, currentUserId]);

	const requests = useMemo(
		() => ({
			announcement: reactAnnouncement,
			level: reactLevelComment,
			profile: reactProfileComment,
		}),
		[],
	);

	const handleReactDocument = useCallback(
		(mode: "like" | "dislike") => {
			if (!document || !["like", "dislike"].includes(mode)) return;

			// Auth / permission checks
			if (!currentUserId || userIsMuted) {
				const errorMessages = {
					noUser: "You must log in to do that.",
					muted: "You can't use this feature while muted.",
				};

				let errorMessage = errorMessages.noUser;
				if (userIsMuted) errorMessage = errorMessages.muted;

				toast.error(errorMessage);
				return;
			}

			if (document?.author?._id === currentUserId) {
				return toast.error(
					"You cannot react to yourself! Well, you technically can... but NO!",
				);
			}

			const msg = toast.loading("Processing...");
			requests[type](document._id, mode)
				.then((response) => {
					const likes = response?.data?.likes;
					const dislikes = response?.data?.dislikes;

                    setLikeCount(likes?.length);
                    setDislikeCount(dislikes?.length);
                    setIsDisliked(dislikes?.includes(currentUserId) || false);
                    setIsLiked(likes?.includes(currentUserId) || false);

					// update the matching document
					setDocuments((prev) =>
						prev?.map((item) =>
							item._id === document._id
								? {
										...item,
										user_likes: likes,
										user_dislikes: dislikes,
									}
								: item,
						),
					);
				})
				.finally(() => toast.dismiss(msg));
		},
		[currentUserId, document, userIsMuted, setDocuments, requests, type],
	);

	return (
		<div className="comment-reaction-container">
			<ReactButton
				type="like"
				active={isLiked}
				count={likeCount}
				handleReactDocument={handleReactDocument}
			/>
			<ReactButton
				type="dislike"
				active={isDisliked}
				count={dislikeCount}
				handleReactDocument={handleReactDocument}
			/>
		</div>
	);
};

const ReactButton = ({ type, active, count, handleReactDocument }) => {
	const activeClass = active ? type : "";
	const iconClass = active
		? "material-symbols-rounded"
		: "material-symbols-outlined";
	const iconName = type === "like" ? "thumb_up" : "thumb_down";

	return (
		<button
			className={`${activeClass}`}
			onClick={() => handleReactDocument(type)}>
			<span className={`flex-center notranslate ${iconClass}`}>
				{iconName}
			</span>
			<span className="flex-center">{count}</span>
		</button>
	);
};

export default CommentReaction;
